import { useState, type FormEvent, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { asApiError, type ApiError } from '@/api/errors';
import { useAuth } from '@/auth/useAuth';
import { describeApiError } from '@/i18n/errorMessages';
import { useI18n } from '@/i18n/useI18n';
import { HOME_PATH } from '@/layout/nav';
import { Button } from '@/ui/Button';
import { Card, CardBody } from '@/ui/Card';
import { Marca } from '@/ui/Marca';

/**
 * Ingreso.
 *
 * Pide **marca + correo + contraseña**: el correo es único por tenant, no
 * globalmente (ver `services/api/routers/auth.py`). La misma consultora puede
 * trabajar para dos laboratorios con el mismo correo.
 *
 * El error de credenciales se muestra tal cual lo manda la API —un solo
 * mensaje para los tres fallos posibles— porque distinguirlos permitiría
 * enumerar las cuentas de un cliente. No inventes un "esa marca no existe".
 */
export function LoginPage(): ReactNode {
  const { t } = useI18n();
  const { login, isAuthenticated, endedReason } = useAuth();
  const location = useLocation();

  const [tenant, setTenant] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<ApiError | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const from = (location.state as { from?: string } | null)?.from;

  if (isAuthenticated) {
    return <Navigate to={from ?? HOME_PATH} replace />;
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (tenant === '' || email === '' || password === '') return;
    setSubmitting(true);
    setError(null);
    login({ tenant: tenant.trim(), email: email.trim(), password })
      .catch((cause: unknown) => {
        setError(asApiError(cause));
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const described = error === null ? null : describeApiError(error, t);
  const sessionNotice =
    endedReason === 'reused'
      ? t('auth.session.reused')
      : endedReason === 'expired'
        ? t('auth.session.expired')
        : endedReason === 'revoked'
          ? t('auth.session.revoked')
          : null;

  return (
    <main className="flex min-h-screen items-center justify-center bg-surface-sunken p-4">
      <Card className="w-full max-w-sm">
        <CardBody className="space-y-5">
          <div>
            {/*
              El ingreso es la única pantalla del panel donde la marca aparece
              entera y con su degradado. Adentro el color es estado —verde
              verificado, ámbar sospecha, rojo crítico— y la marca se retira a
              una firma en la barra superior. Acá no hay ningún estado que
              mirar, así que puede firmar.
            */}
            <Marca alto={26} />
            <h1 className="mt-1 text-lg font-semibold text-ink">{t('auth.login.title')}</h1>
            <p className="mt-1 text-sm text-ink-muted">{t('auth.login.intro')}</p>
          </div>

          {sessionNotice !== null && (
            // Un cierre por reutilización de token no es una molestia de UI:
            // es la señal de una posible filtración y tiene que verse.
            <p
              role="status"
              className={
                endedReason === 'reused'
                  ? 'rounded border border-critical/30 bg-critical-soft p-3 text-sm text-ink'
                  : 'rounded border border-border bg-surface-sunken p-3 text-sm text-ink-muted'
              }
            >
              {sessionNotice}
            </p>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <Field
              id="tenant"
              label={t('auth.login.tenant')}
              hint={t('auth.login.tenantHint')}
              value={tenant}
              onChange={setTenant}
              autoComplete="organization"
              required
            />
            <Field
              id="email"
              type="email"
              label={t('auth.login.email')}
              value={email}
              onChange={setEmail}
              autoComplete="username"
              required
            />
            <Field
              id="password"
              type="password"
              label={t('auth.login.password')}
              value={password}
              onChange={setPassword}
              autoComplete="current-password"
              required
            />

            {described !== null && (
              <p
                role="alert"
                className="rounded border border-critical/30 bg-critical-soft p-3 text-sm text-ink"
              >
                {described.title}
                {described.traceId !== null && (
                  <span className="mt-1 block text-xs text-ink-subtle">
                    {t('state.error.trace')}:{' '}
                    <code className="font-mono select-all">{described.traceId}</code>
                  </span>
                )}
              </p>
            )}

            <Button type="submit" variant="primary" className="w-full" disabled={submitting}>
              {submitting ? t('auth.login.submitting') : t('auth.login.submit')}
            </Button>
          </form>
        </CardBody>
      </Card>
    </main>
  );
}

function Field({
  id,
  label,
  hint,
  value,
  onChange,
  type = 'text',
  autoComplete,
  required,
}: {
  id: string;
  label: string;
  hint?: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  autoComplete?: string;
  required?: boolean;
}): ReactNode {
  const hintId = `${id}-hint`;
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-sm font-medium text-ink">
        {label}
      </label>
      <input
        id={id}
        name={id}
        type={type}
        value={value}
        required={required}
        autoComplete={autoComplete}
        {...(hint === undefined ? {} : { 'aria-describedby': hintId })}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        className="w-full rounded border border-border-strong bg-surface px-3 py-2 text-sm text-ink"
      />
      {hint !== undefined && (
        <p id={hintId} className="text-xs text-ink-subtle">
          {hint}
        </p>
      )}
    </div>
  );
}
